import { useMemo, useState } from "react";
import {
  adminDangerGhostBtnClass,
  adminGhostBtnClass,
  adminIndigoGhostBtnClass,
  adminInputClass,
  cardTitleTextClass,
  salesIndigoOutlineBtnClass,
  sectionCardClass,
} from "@/components/common/surfaceClasses";
import { ORG_ROLE_LABELS, orgRolesForAddDropdown, type OrgRole } from "./adminTypes";
type GeneralSupportPanelProps = {
  generalFallback: {
    columns_available: boolean;
    is_valid: boolean;
    warning: string | null;
    source_id: string | null;
  };
  generalFallbackChoice: string;
  setGeneralFallbackChoice: (value: string) => void;
  generalFallbackBusy: boolean;
  fallbackCandidates: Array<{ key: string; label: string }>;
  onSave: () => void;
  onClear: () => void;
};
export function AdminContactsGeneralSupportPanel(props: GeneralSupportPanelProps) {
  const {
    generalFallback,
    generalFallbackChoice,
    setGeneralFallbackChoice,
    generalFallbackBusy,
    fallbackCandidates,
    onSave,
    onClear,
  } = props;
  return (
    <div
      className={
        "rounded-xl border px-4 py-3 space-y-3 " +
        (generalFallback.columns_available && generalFallback.is_valid
          ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/70 dark:bg-emerald-950/30"
          : "border-amber-200 bg-amber-50/80 dark:border-amber-900/70 dark:bg-amber-950/30")
      }
    >
      <div>
        <div className="font-semibold text-gray-900 dark:text-gray-100">General support fallback</div>
        <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
          {generalFallback.warning ||
            "General support contact configured. Weak or ambiguous contact answers can safely fall back here."}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className={`min-w-[280px] flex-1 ${adminInputClass}`}
          value={generalFallbackChoice}
          onChange={(e) => setGeneralFallbackChoice(e.target.value)}
          disabled={generalFallbackBusy}
        >
          <option value="">Pick org-scoped active contact...</option>
          {fallbackCandidates.map((candidate) => (
            <option key={candidate.key} value={candidate.key}>
              {candidate.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onSave}
          disabled={generalFallbackBusy || !generalFallback.columns_available || !generalFallbackChoice}
          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          {generalFallbackBusy ? "Saving..." : "Set general support contact"}
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={generalFallbackBusy || !generalFallback.columns_available || !generalFallback.source_id}
          className={`px-3 py-1.5 disabled:opacity-50 ${adminGhostBtnClass}`}
        >
          Clear
        </button>
      </div>
      <p className="text-[11px] text-gray-600 dark:text-gray-300">
        The selected contact is automatically tagged with <span className="font-mono">general_support</span>.
      </p>
    </div>
  );
}

type TagsPanelProps = {
  pathSource: string;
  setPathSource: (value: string) => void;
  pathTarget: string;
  setPathTarget: (value: string) => void;
  availablePaths: string[];
  topicSource: string;
  setTopicSource: (value: string) => void;
  topicTarget: string;
  setTopicTarget: (value: string) => void;
  availableTopics: string[];
  onMergePath: () => void;
  onDeletePath: () => void;
  onMergeTopic: () => void;
  onDeleteTopic: () => void;
  onSuggestMissingTags: () => Promise<string[]>;
  onAddTopicCoverageTag: (tag: string) => Promise<void>;
};
export function AdminContactsTagsPanel(props: TagsPanelProps) {
  const {
    pathSource,
    setPathSource,
    pathTarget,
    setPathTarget,
    availablePaths,
    topicSource,
    setTopicSource,
    topicTarget,
    setTopicTarget,
    availableTopics,
    onMergePath,
    onDeletePath,
    onMergeTopic,
    onDeleteTopic,
    onSuggestMissingTags,
    onAddTopicCoverageTag,
  } = props;
  const [aiExpanded, setAiExpanded] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSuggestedTags, setAiSuggestedTags] = useState<string[]>([]);
  const [addingTag, setAddingTag] = useState<string | null>(null);
  const missingSuggestions = useMemo(() => {
    const existing = new Set(availableTopics.map((t) => t.toLowerCase()));
    return aiSuggestedTags.filter((t) => !existing.has(t.toLowerCase()));
  }, [aiSuggestedTags, availableTopics]);
  const runAiSuggest = async () => {
    setAiBusy(true);
    setAiError(null);
    try {
      const tags = await onSuggestMissingTags();
      setAiSuggestedTags(tags);
      setAiExpanded(true);
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(false);
    }
  };
  const addSuggestedTag = async (tag: string) => {
    setAddingTag(tag);
    setAiError(null);
    try {
      await onAddTopicCoverageTag(tag);
      setAiSuggestedTags((prev) => prev.filter((t) => t !== tag));
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e));
    } finally {
      setAddingTag(null);
    }
  };
  const closeAiSuggestions = () => {
    setAiExpanded(false);
    setAiSuggestedTags([]);
    setAiError(null);
    setAddingTag(null);
  };
  return (
    <div className={`${sectionCardClass} space-y-3`}>
      <h3 className={`font-semibold ${cardTitleTextClass}`}>Path and topic tags</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700 dark:text-zinc-300">Manage contact paths</div>
          <select className={`w-full ${adminInputClass}`} value={pathSource} onChange={(e) => setPathSource(e.target.value)}>
            <option value="">Source path...</option>
            {availablePaths.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input className={`w-full ${adminInputClass}`} placeholder="Merge into path..." value={pathTarget} onChange={(e) => setPathTarget(e.target.value)} />
          <div className="flex gap-2">
            <button type="button" onClick={onMergePath} className={`px-3 py-1.5 ${adminGhostBtnClass}`}>Merge path</button>
            <button type="button" onClick={onDeletePath} className={`px-3 py-1.5 ${adminDangerGhostBtnClass}`}>Delete path</button>
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700 dark:text-zinc-300">Manage topic tags</div>
          <select className={`w-full ${adminInputClass}`} value={topicSource} onChange={(e) => setTopicSource(e.target.value)}>
            <option value="">Source topic...</option>
            {availableTopics.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <input className={`w-full ${adminInputClass}`} placeholder="Merge into topic..." value={topicTarget} onChange={(e) => setTopicTarget(e.target.value)} />
          <div className="flex gap-2">
            <button type="button" onClick={onMergeTopic} className={`px-3 py-1.5 ${adminGhostBtnClass}`}>Merge topic</button>
            <button type="button" onClick={onDeleteTopic} className={`px-3 py-1.5 ${adminDangerGhostBtnClass}`}>Delete topic</button>
          </div>
        </div>
      </div>
      <div className="pt-2 border-t border-gray-100 dark:border-zinc-800">
        <button
          type="button"
          className="w-full rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-800 text-xs font-semibold px-3 py-1.5 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:bg-indigo-950/55"
          onClick={() => {
            if (!aiExpanded) setAiExpanded(true);
            void runAiSuggest();
          }}
          disabled={aiBusy}
        >
          {aiBusy ? "Scanning KB for missing tags..." : "Ask AI to generate missing tags for my organization"}
        </button>
        {aiExpanded ? (
          <div className="mt-2 rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2 space-y-2 dark:border-indigo-800/60 dark:bg-indigo-950/30">
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs text-indigo-900 dark:text-indigo-200">
                Suggested tags are deduped against your current topic tags. Click a tag to add it to org coverage.
              </div>
              <button
                type="button"
                onClick={closeAiSuggestions}
                className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] ${salesIndigoOutlineBtnClass}`}
              >
                Close
              </button>
            </div>
            {aiError ? <div className="text-xs text-red-700 dark:text-red-400">{aiError}</div> : null}
            {missingSuggestions.length === 0 ? (
              <div className="text-xs text-gray-600 dark:text-zinc-400">No missing tags suggested right now.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {missingSuggestions.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => void addSuggestedTag(tag)}
                    disabled={addingTag === tag}
                    className={`px-2 py-1 rounded-full text-xs ${adminIndigoGhostBtnClass} disabled:opacity-50`}
                    title="Click to add this topic tag coverage"
                  >
                    {addingTag === tag ? `Adding ${tag}...` : `+ ${tag}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

type InvitePanelProps = {
  inviteTargetName: string;
  inviteEmail: string;
  setInviteEmail: (value: string) => void;
  inviteRole: OrgRole;
  setInviteRole: (value: OrgRole) => void;
  inviteBusy: boolean;
  onSend: () => void;
  onClose: () => void;
};
export function AdminContactsInvitePanel(props: InvitePanelProps) {
  const { inviteTargetName, inviteEmail, setInviteEmail, inviteRole, setInviteRole, inviteBusy, onSend, onClose } = props;
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 space-y-2 dark:border-indigo-800/70 dark:bg-indigo-950/30">
      <h3 className="font-semibold text-indigo-950 dark:text-indigo-100">Invite and link: {inviteTargetName}</h3>
      <div className="flex flex-wrap gap-2 items-center">
        <input type="email" className={`${adminInputClass} border-indigo-200 dark:border-indigo-800/60 flex-1 min-w-48`} placeholder="Email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
        <select className={`${adminInputClass} border-indigo-200 dark:border-indigo-800/60`} value={inviteRole} onChange={(e) => setInviteRole(e.target.value as OrgRole)}>
          {orgRolesForAddDropdown(false).map((r) => (
            <option key={r} value={r}>{ORG_ROLE_LABELS[r]}</option>
          ))}
        </select>
        <button type="button" disabled={inviteBusy} onClick={onSend} className="px-3 py-1.5 rounded-lg bg-indigo-700 text-white text-sm disabled:opacity-50">
          {inviteBusy ? "Sending…" : "Send invite + migrate"}
        </button>
        <button type="button" onClick={onClose} className={`px-3 py-1.5 ${adminIndigoGhostBtnClass} rounded-lg text-sm`}>Close</button>
      </div>
    </div>
  );
}

type MigratePanelProps = {
  migrateTargetName: string;
  migrateEmail: string;
  setMigrateEmail: (value: string) => void;
  migrateProfileId: string;
  setMigrateProfileId: (value: string) => void;
  onMigrate: () => void;
  onClose: () => void;
};
export function AdminContactsMigratePanel(props: MigratePanelProps) {
  const { migrateTargetName, migrateEmail, setMigrateEmail, migrateProfileId, setMigrateProfileId, onMigrate, onClose } = props;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 space-y-2 dark:border-amber-800/70 dark:bg-amber-950/30">
      <h3 className="font-semibold text-amber-950 dark:text-amber-200">Migrate external → user directory: {migrateTargetName}</h3>
      <div className="flex flex-wrap gap-2">
        <input type="email" inputMode="email" autoComplete="email" className={`${adminInputClass} border-amber-200 dark:border-amber-800/60 flex-1 min-w-40`} placeholder="Profile email" value={migrateEmail} onChange={(e) => setMigrateEmail(e.target.value)} />
        <input className={`${adminInputClass} border-amber-200 dark:border-amber-800/60 flex-1 min-w-48`} placeholder="Or profile UUID" value={migrateProfileId} onChange={(e) => setMigrateProfileId(e.target.value)} />
        <button type="button" onClick={onMigrate} className="px-3 py-1.5 rounded-lg bg-amber-700 text-white text-sm">Migrate</button>
        <button type="button" onClick={onClose} className={`px-3 py-1.5 ${adminGhostBtnClass} border-amber-300 dark:border-amber-700`}>Close</button>
      </div>
    </div>
  );
}
